/**
 * **전송 오류의 뜻 — 정본 하나**(D-105). 오류 코드가 «이 실패가 무엇인가» 를 말한다. 무엇을 할지(처분)는 부르는 쪽이 이 뜻에서 정한다:
 * 세션 스트림(`sessionStreamMux` — 다시 붙기·세션 소실·인증 기다림·멈춤), 실행 중 값 폴링(`pollFailure` — 잇기·멈춤·인증 기다림·알리기),
 * 실행 시작의 «결과 모름»(`simulationManagerService.build`), «대상이 없다» 판정(이력·결과 파일).
 * 전에는 스트림 표와 폴링 표가 같은 코드를 따로 셈했다 — 인증 만료가 스트림에서는 «기다림», 폴링에서는 «오류로 알림» 이었고(인증 서버
 * 장애 중 폴링이 매 주기 «Token has expired» 를 올렸다), «답을 잃었다» 판정도 자리마다 달랐다.
 *
 * 사용자에게 보일 문장은 `transportError`(코드 → 문장)가 맡는다 — 이 표는 문장을 짓지 않는다.
 */
import { Code, ConnectError } from '@connectrpc/connect';
import { isUnreachable } from './transportError';

export type TransportMeaning =
  /** 답을 잃었을 수 있다 — 서버가 지금 받지 못했거나(혼잡·준비 중·게이트웨이 429·502·503·504) 브라우저가 닿지 못했거나 시간 안에 답이 오지
   *  않았다. 요청이 서버에서 처리됐는지 모른다. 다시 하면 될 수 있다 */
  | 'disconnected'
  /** 서버가 받지 않았다(자원 부족·경합으로 물러남) — 처리되지 않았다. 다시 하면 될 수 있다 */
  | 'busy'
  /** 서버가 받아 처리하다 실패했다(내부 오류·모르는 실패·취소) — 다시 하면 될 수도, 같은 실패일 수도 있다 */
  | 'fault'
  /** 인증이 만료됐다 — 인증이 새로워지면 된다(인증 서버 장애 중에는 그때까지 기다린다) */
  | 'auth-expired'
  /** 대상이 서버에 없다(세션·태스크·파일·행이 사라졌다) */
  | 'gone'
  /** 다시 해도 같다(권한·인자·미지원·전제 조건·이미 있음·자료 손실) */
  | 'refused';

/** 코드 → 뜻 — 코드 전부를 적는다(빠지면 타입이 막는다) */
const MEANING_BY_CODE: Readonly<Record<Code, TransportMeaning>> = {
  [Code.Canceled]: 'fault',
  [Code.Unknown]: 'fault',
  [Code.InvalidArgument]: 'refused',
  [Code.DeadlineExceeded]: 'disconnected',
  [Code.NotFound]: 'gone',
  [Code.AlreadyExists]: 'refused',
  [Code.PermissionDenied]: 'refused',
  [Code.ResourceExhausted]: 'busy',
  [Code.FailedPrecondition]: 'refused',
  [Code.Aborted]: 'busy',
  [Code.OutOfRange]: 'refused',
  [Code.Unimplemented]: 'refused',
  [Code.Internal]: 'fault',
  [Code.Unavailable]: 'disconnected',
  [Code.DataLoss]: 'refused',
  [Code.Unauthenticated]: 'auth-expired',
};

/**
 * 실패의 뜻. 닿지 못함(fetch 의 TypeError — connect-web 은 Unknown 으로 감싼다)은 «답을 잃었을 수 있다».
 * 전송 오류가 아닌 실패(코드 없음)는 모르는 실패 — «서버 실패» 와 같이 다룬다.
 */
export function transportMeaningOf(err: unknown): TransportMeaning {
  if (!(err instanceof ConnectError)) return 'fault';
  if (isUnreachable(err)) return 'disconnected';
  return MEANING_BY_CODE[err.code];
}
